import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	ViewRef,
	ElementRef,
	EventEmitter,
	Output,
	AfterViewInit
} from '@angular/core';
import { debounceTime, filter, fromEvent, Observable, Subject, take, takeUntil, map, timer, of, switchMap } from 'rxjs';
import type { Session } from '@go-modules/services/session-list-datasource/session-list.datasource';
import {
	SelectedService,
	selectedServiceToken
} from 'go-modules/services/selected/selected.service';
import { EnvironmentVarsService } from '@go-modules/services/environment-vars/environment-vars.service';
import { MODES } from '../comment-reactions/comment-reactions.component';
import { CourseFolder } from '@go-modules/services/course/interfaces/course-folder.interface';
import { CommentListService, ProcessedComment } from '@go-modules/services/comment-list/comment-list.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { NgxCommentService } from '@go-modules/services/comment/comment.service';
import { commentModelToken, CommentModel } from 'go-modules/models/comment/comment.factory';
import type { PlayerSync } from '@go-modules/interfaces/media/media';
import type { Marker } from '@go-modules/interfaces/markers/marker';
import { TranslateService } from '@ngx-translate/core';
import { colors } from '@go-modules/interfaces/colors';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { FormatTimePipe } from '@go-modules/pipes/format-time/format-time.pipe';
import { FormatDatePipe } from '@go-modules/pipes/format-date/format-date.pipe';
import { COMMENT_RESOURCE_TYPES } from 'go-modules/models/comment/comment.constant';
import type { Media } from 'go-modules/services/attachment/media';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AllPlayersService } from '@go-modules/services/all-players/all-players.service';
import { EventService, GoEvent } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { VIRTUAL_SCROLL_STRATEGY } from '@angular/cdk/scrolling';
import type { Comment } from '@go-modules/interfaces/comments/comment';
import { CommentScrollStrategy } from '@go-modules/components/feedback-session/comment-viewer/comment-scroller/comment-scroll-strategy';
import { FeedbackSessionService } from '@go-modules/services/feedback-session/feedback-session.service';
import { TimeUtils } from '@go-modules/utilities/time/time-utils';
import { LibraryService } from '@go-modules/services/library/library.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { CollectionDestinationComponent } from '@go-modules/components/library/collection-destination/collection-destination.component';
import type { CollectionDestinationData } from '@go-modules/components/library/collection-destination/collection-destination.component';
import { DESTINATION_MODES } from '@go-modules/components/library/collection-destination/collection-destination.constants';
import { CONTENT_TYPES, LIBRARY_TYPES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';

export const SEEK_OFFSET = 5000;

@Component({
	selector: 'comment-viewer',
	templateUrl: './comment-viewer.component.html',
	styleUrls: ['./comment-viewer.component.scss'],
	providers: [FormatTimePipe, FormatDatePipe,
		CommentScrollStrategy,
		{
			provide: VIRTUAL_SCROLL_STRATEGY,
			useExisting: CommentScrollStrategy
		}
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class CommentViewerComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() public playerSync: PlayerSync;
	@Input() public viewFrom: string;
	@Input() public feedbackSettings;
	@Input() public tags: Marker[];
	@Input() public hideCommentReply: boolean;
	@Output() public onEdit: EventEmitter<any>  = new EventEmitter();
	@Output() public addAiAssistant: EventEmitter<void> = new EventEmitter();
	public commentReactionModes = MODES;
	public mediaPreviewOptions = {
		autoPlay: false,
		darkMode: true,
		showFullscreenOption: true
	};
	public environmentVarsService: EnvironmentVarsService;
	private playerSyncCallback?;
	public selectedComment: ProcessedComment;
	public editingCommentIds: (number | string)[] = [];
	public editingCommentTime: (number | string)[] = [];
	public activeCommentIds: (number|string)[] = [];
	public ltiReadOnly: boolean;
	public group: CourseFolder;
	public session: Session;
	public totalDuration: number;
	public comments$: Observable<ProcessedComment[]>;
	public autoScrollEnabled: boolean = true;
	private previousCommentIds: (number|string)[] = [];
	private destroy$ = new Subject<void>();
	private scrollSubscription: any;
	private scrollToComment$ = new Subject<ProcessedComment>();
	private scrollViewRequest$ = new Subject<void>();
	private pendingCommentIds = new Set<string>();
	private calculatedValuesCache = new Map<number|string, {
		shouldDisplayTimeCode?: boolean;
		ariaLabel?: string;
		mayEdit?: boolean;
		isFirstEndNote?: boolean;
		isSyncEventType?: boolean;
	}>();

	constructor (
		public cdr: ChangeDetectorRef,
		public commentListService: CommentListService,
		private commentService: NgxCommentService,
		private translate: TranslateService,
		private formatTimePipe: FormatTimePipe,
		private formatDatePipe: FormatDatePipe,
		private dialog: MatDialog,
		private modal: GoModalService,
		private libraryService: LibraryService,
		private ngxGoToastService: NgxGoToastService,
		private elementRef: ElementRef,
		private allPlayersService: AllPlayersService,
		private eventService: EventService,
		private scrollStrategy: CommentScrollStrategy,
		private feedbackSessionService: FeedbackSessionService,
		private timeUtils: TimeUtils,
		@Inject(selectedServiceToken) public selectedService: SelectedService,
		@Inject(userServiceToken) public userService: UserService,
		@Inject(commentModelToken) private Comment: ReturnType<typeof CommentModel>,
		@Inject(mediaToken) private MediaModel
	) {
		this.environmentVarsService = EnvironmentVarsService.getInstance();
	}

	public ngOnInit () {
		this.group = this.selectedService.getGroup();
		this.session = this.selectedService.getSession();
		this.ltiReadOnly = this.environmentVarsService.get(EnvironmentVarsService.VAR.READONLY);

		this.commentListService.comments$.pipe(
			takeUntil(this.destroy$)
		).subscribe((comments) => {
			this.handlePlayerSync(comments);
			this.handleNewComment(comments);
			this.scrollStrategy.updateComments(comments);
			this.calculatedValuesCache.clear();

			// mark initial comments as viewed, after comments have rendered
			timer(0).pipe(takeUntil(this.destroy$)).subscribe(() => {
				this.onScroll();
			});
		});

		this.eventService.events
			.pipe(
				takeUntil(this.destroy$),
				filter((ev: GoEvent) => ev.name === EVENT_NAMES.MEDIA_SYNC))
			.subscribe((ev: GoEvent) => {
				this.commentListService.comments$.pipe(take(1)).subscribe((comments) => {
					const comment = comments.find((c) => {
						return c.media && c.media.some((m) => m.item.media_id === ev.data.media_id);
					});

					if (comment) {
						const updatedResource = comment.resource.map((r) =>
							r.resource_type === COMMENT_RESOURCE_TYPES.MEDIA &&
							r.item.media_id === ev.data.media_id
								? { ...r, item: this.MediaModel.model(ev.data) }
								: r
						);

						this.commentListService.editComment(comment, { resource: updatedResource });
					}
				});
			});

		// Handle scrolling to comments
		this.scrollToComment$.pipe(
			takeUntil(this.destroy$),
			debounceTime(50)
		).subscribe((comment) => {
			this.performScrollToComment(comment);
		});

		this.scrollViewRequest$.pipe(
			takeUntil(this.destroy$),
			switchMap(() => timer(3000).pipe(
				takeUntil(this.scrollViewRequest$)
			))
		).subscribe(() => {
			this.processPendingCommentViews();
		});
	}

	public ngOnDestroy () {
		this.destroy$.next();
		this.destroy$.complete();
		this.clearPlayerSyncEvents();
		if (this.scrollSubscription) {
			this.scrollSubscription.unsubscribe();
		}
	}

	public ngAfterViewInit () {
		this.totalDuration = this.playerSync.getDuration();

		const feedbackDisplay = this.elementRef.nativeElement.querySelector('cdk-virtual-scroll-viewport');
		if (feedbackDisplay) {
			this.scrollSubscription = fromEvent(feedbackDisplay, 'scroll')
				.pipe(
					takeUntil(this.destroy$)
				)
				.subscribe(() => {
					this.onScroll();
				});
		}

		this.elementRef.nativeElement.addEventListener('mouseover', () => {
			this.autoScrollEnabled = false;
		});
		this.elementRef.nativeElement.addEventListener('mouseleave', () => {
			// if any cdk overlay is open, don't enable autoscroll
			// since user is interacting with the feedback
			const overlayContainer = document.querySelector('.cdk-overlay-container');
			if (overlayContainer && overlayContainer.children.length > 0) {
				this.autoScrollEnabled = false;
			} else {
				this.autoScrollEnabled = true;
			}
		});
	}

	public onScroll (): void {
		const commentElements = this.elementRef.nativeElement.querySelectorAll('[data-comment-id]');
		const feedbackDisplay = this.elementRef.nativeElement.querySelector('cdk-virtual-scroll-viewport');

		if (!feedbackDisplay || !commentElements.length) {
			return;
		}

		const feedbackRect = feedbackDisplay.getBoundingClientRect();

		this.commentListService.comments$.pipe(take(1)).subscribe((comments) => {
			Array.from(commentElements).forEach((el: Element) => {
				const commentId = el.getAttribute('data-comment-id');
				const rect = el.getBoundingClientRect();

				// Check if comment is within the visible area (minus 1 to account for discrepancy w/ last comment)
				// Approximate bottom as top + 56px so comment children aren't included
				if (rect.top >= feedbackRect.top && (rect.top + 56) <= feedbackRect.bottom) {
					// Search through comments for commentId
					const comment = comments.find((comm) => Number(comm.comment_id) === Number(commentId));

					if (!comment) return;

					let alreadyViewed = false;
					if (comment.views) {
						alreadyViewed = comment.views.some((e) =>
							e.user_id.toString() === this.userService.currentUser.user_id.toString()
						);
					}

					if (!this.pendingCommentIds.has(commentId) && !comment.viewed_by_me && !alreadyViewed) {
						this.pendingCommentIds.add(commentId);

						if (comment.views) {
							comment.views.push({ user_id: this.userService.currentUser.user_id });
						} else {
							comment.views = [{ user_id: this.userService.currentUser.user_id }];
						}
						comment.viewed_by_me = true;

						// remove classes after 1 second so they have time to see it
						timer(1000).pipe(takeUntil(this.destroy$)).subscribe(() => {
							this.commentListService.editComment(comment, { viewed_by_me: true, views: comment.views });
						});
					}
				}
			});

			this.scrollViewRequest$.next();
		});
	}

	private processPendingCommentViews (): void {
		if (this.pendingCommentIds.size === 0) {
			return;
		}

		const commentIds = Array.from(this.pendingCommentIds).map((id) => Number(id));
		this.pendingCommentIds.clear();

		if (commentIds.length) {
			this.commentService.saveCommentViews(this.session.session_id, commentIds).subscribe();
		}
	}

	public shouldDisplayTimeCode (comment: ProcessedComment): boolean {
		const cachedValue = this.calculatedValuesCache.get(comment.comment_id);
		if (cachedValue && 'shouldDisplayTimeCode' in cachedValue) {
			return cachedValue.shouldDisplayTimeCode;
		}
		const activity = this.selectedService.getActivity();
		const result = comment.time !== null &&
			!comment.parent_id &&
			!activity.isConversationBoard();
		this.calculatedValuesCache.set(comment.comment_id, { ...cachedValue, shouldDisplayTimeCode: result });
		return result;
	};

	public onCommentClick (comment: ProcessedComment): void {
		this.setSelectedComment(comment);
		const offset = this.isSyncEventType(comment) ? 0 : SEEK_OFFSET;
		this.playerSync.seek(Number(comment.time) - offset);
	}

	public setSelectedComment (comment: ProcessedComment, event?: MouseEvent): void {
		if (event) {
			event.stopPropagation();
		}
		// clear cache for old and new comment so that aria-label is recalculated
		this.calculatedValuesCache.delete(comment.comment_id);
		if (this.selectedComment) {
			this.calculatedValuesCache.delete(this.selectedComment.comment_id);
		}
		this.selectedComment = comment;
	}

	public edit (comment: ProcessedComment | Comment): void {
		this.onEdit.emit(true);
		this.editingCommentIds.push(comment.comment_id || comment.temp_id);
		this.scrollToComment(comment as ProcessedComment);
	}

	public cancelEdit (comment: ProcessedComment): void {
		this.onEdit.emit(false);
		this.editingCommentIds = this.editingCommentIds.filter(
			(id) => id !== comment.comment_id && id !== comment.temp_id
		);
		if (!comment.comment_id) {
			// trigger change detection to ensure feedback creator is destroyed properly
			this.cdr.detectChanges();
			// then remove the temp comment
			this.commentListService.removeTempComment(comment);
		}
	}

	public showFeedbackCreator (comment: ProcessedComment): boolean {
		if (this.editingCommentTime.includes(comment.comment_id)) {
			return false;
		}
		if (comment.comment_id) {
			return this.editingCommentIds.includes(comment.comment_id);
		}
		return this.editingCommentIds.includes(comment.temp_id);
	}

	public isFirstEndNote (comment: ProcessedComment): Observable<boolean> {
		const cachedValue = this.calculatedValuesCache.get(comment.comment_id);
		if (cachedValue && 'isFirstEndNote' in cachedValue) {
			return of(cachedValue.isFirstEndNote);
		}
		return this.commentListService.comments$.pipe(
			take(1),
			map((comments) => {
				const firstEndNote = comments.find((c) => c.time === null);
				const result = firstEndNote?.comment_id === comment.comment_id;
				this.calculatedValuesCache.set(comment.comment_id, { ...cachedValue, isFirstEndNote: result });
				return result;
			})
		);
	}

	public editTime (event: MouseEvent, comment: ProcessedComment): void {
		event.stopPropagation();
		this.totalDuration = this.playerSync.getDuration();
		this.editingCommentTime.push(comment.comment_id);
	}

	public isTimeCodeClickDisabled (): boolean {
		const activity = this.selectedService.getActivity();
		const session = this.selectedService.getSession();
		const user = this.userService.currentUser;
		return activity.is_video_seeking_disabled
			&& session.num_my_comments === 0
			&& session.isOwner(user);
	}

	public keepTimeEdit (time: number, comment: ProcessedComment): void {
		this.editingCommentTime = this.editingCommentTime.filter((id) => id !== comment.comment_id);
		this.editingCommentIds = this.editingCommentIds.filter((id) => id !== comment.comment_id);
		const commentToSave = { ...comment, time };
		delete commentToSave.parent;
		delete commentToSave.children;

		this.commentListService.editComment(comment, {time});

		this.commentService.save(this.session.session_id, commentToSave).subscribe();
	}

	public cancelTimeEdit (comment: ProcessedComment): void {
		this.editingCommentTime = this.editingCommentTime.filter((id) => id !== comment.comment_id);
		this.editingCommentIds = this.editingCommentIds.filter((id) => id !== comment.comment_id);
	}

	public isSyncEventType (comment: ProcessedComment): boolean {
		const cachedValue = this.calculatedValuesCache.get(comment.comment_id);
		if (cachedValue && 'isSyncEventType' in cachedValue) {
			return cachedValue.isSyncEventType;
		}
		if (!comment.resource) {
			return false;
		}
		const result = comment.resource.some((res) => {
			return res.resource_type === COMMENT_RESOURCE_TYPES.SYNC_EVENT;
		});
		this.calculatedValuesCache.set(comment.comment_id, { ...cachedValue, isSyncEventType: result });
		return result;
	}

	public mayEdit = (comment: ProcessedComment): boolean => {
		if (this.isEditDisabled()) {
			return false;
		}

		const cachedValue = this.calculatedValuesCache.get(comment.comment_id);
		if (cachedValue && 'mayEdit' in cachedValue) {
			return cachedValue.mayEdit;
		}
		const group = this.selectedService.getGroup();
		const result = (
			group.hasInstructorRole(true) ||
			Number(comment.created_by) === Number(this.userService.currentUser.user_id)
		);
		this.calculatedValuesCache.set(comment.comment_id, { ...cachedValue, mayEdit: result });
		return result;
	};

	public isEditDisabled = (): boolean => {
		const session = this.selectedService.getSession();
		const activity = this.selectedService.getActivity();
		const user = this.userService.currentUser;
		if (session.isLimitCommentOnlySingleAttempt(activity, user)) {
			return true;
		}

		const group = this.selectedService.getGroup();
		const license = this.selectedService.getLicense();
		return !group.validateCanCreateSession(user, license).isValid;
	};


	public deleteMediaResource (comment: ProcessedComment, media: Media) {
		const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: this.translate.instant('feedback-session-feedback-tree-comment_sure-remove-media-title'),
				message: this.translate.instant('feedback-session-feedback-tree-comment_sure-remove-media'),
				confirmText: this.translate.instant('common_continue')
			} as ConfirmDialogData
		});

		dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirm) => {
			if (confirm) {
				const commentToSave = { ...comment };
				commentToSave.resource = commentToSave.resource
					.filter((res) => {
						return res.resource_type !== COMMENT_RESOURCE_TYPES.MEDIA
							|| res.item.media_id !== media.media_id;
					});

				if(commentToSave.resource.length === 0 && !commentToSave.text) {
					// Delete main comment itself if no resource/s and no text linked to comment
					this.commentListService.removeComment(comment);
					this.commentService.deleteComment(this.session.session_id, comment.comment_id).subscribe();
				} else {
					// Immediately save the deletion after confirming when in non-edit comment mode
					delete commentToSave.parent;
					delete commentToSave.children;
					this.commentService.save(this.session.session_id, commentToSave)
						.pipe(takeUntil(this.destroy$))
						.subscribe((res) => {
							const modeledComment = this.Comment.newInstance(res);
							this.commentListService.replaceComment(comment.comment_id, modeledComment);
						});
				}
			}
		});
	}

	public download ($event: MouseEvent, media: Media) {
		$event.preventDefault();
		this.MediaModel.download(media.media_id);
	}

	public canAddToLibrary (media: Media): boolean {
		if (!media || this.ltiReadOnly) {
			return false;
		}

		if (!this.group?.hasInstructorRole(true)) {
			return false;
		}

		const currentUserId = Number(this.userService.currentUser?.user_id);
		if (!currentUserId || Number(media.created_by) !== currentUserId) {
			return false;
		}

		if (media.media_status !== this.MediaModel.READY) {
			return false;
		}

		return media.media_type === this.MediaModel.TYPE.VIDEO ||
			media.media_type === this.MediaModel.TYPE.AUDIO;
	}

	public addToLibrary (media: Media): void {
		if (!this.canAddToLibrary(media)) {
			return;
		}

		if (!media?.media_id) {
			this.showAddToLibraryErrorToast('feedback-session-feedback-tree-comment_add-to-library-error-no-media');
			return;
		}

		this.libraryService.getCollections()
			.pipe(take(1))
			.subscribe({
				next: (collections) => {
					if (!collections || collections.length === 0) {
						this.showAddToLibraryErrorToast('feedback-session-feedback-tree-comment_add-to-library-error-no-collections');
						return;
					}

					const selectedCollection = collections.find((collection) =>
						collection.type === LIBRARY_TYPES.MINE) ?? collections[0];

					const itemType = this.getLibraryItemType(media);
					const destinationData: CollectionDestinationData = {
						collections,
						selectedCollection,
						contentType: itemType,
						mode: DESTINATION_MODES.ADD,
						allowSkip: false,
						submitText: 'common_save'
					};

					this.modal.open(CollectionDestinationComponent, false, {
						data: destinationData
					}).afterClosed().pipe(take(1)).subscribe((res) => {
						if (!res || res.dismissed) {
							return;
						}

						if (!res.collection?.id) {
							this.showAddToLibraryErrorToast('feedback-session-feedback-tree-comment_add-to-library-error-no-collection-selected');
							return;
						}

						const collectionsPayload = [{
							collection_id: res.collection.id
						}] as { collection_id: number; folder_id?: number }[];

						if (res.folder?.id) {
							collectionsPayload[0].folder_id = res.folder.id;
						}

						this.libraryService.copyCollectionItem({
							items: [
								{
									item: {
										item_type: itemType,
										item_id: media.media_id
									},
									collections: collectionsPayload
								}
							]
						}).pipe(take(1)).subscribe({
							next: () => {
								this.ngxGoToastService.createToast({
									type: GoToastStatusType.SUCCESS,
									message: 'library-collections_copied'
								});
							},
							error: () => {
								this.showAddToLibraryErrorToast('feedback-session-feedback-tree-comment_add-to-library-error-copy-failed');
							}
						});
					});
				},
				error: () => {
					this.showAddToLibraryErrorToast('feedback-session-feedback-tree-comment_add-to-library-error-load-collections');
				}
			});
	}

	public getAriaLabel (comment: ProcessedComment): string {
		const cachedValue = this.calculatedValuesCache.get(comment.comment_id);
		if (cachedValue && cachedValue.ariaLabel) {
			return cachedValue.ariaLabel;
		}

		let translation = this.translate.instant('feedback-item_new-aria-label', {
			time: this.formatTimePipe.transform(comment.time),
			name: comment.fullname
		});
		if (this.selectedComment?.comment_id === comment.comment_id) {
			translation += `, ${this.formatDatePipe.transform(comment.created_at)}`;
		}
		if (comment.markers) {
			comment.markers.forEach((marker) => {
				const deprecatedColors = [
					{hex: 'ffcc00', name: 'Tangerine Yellow', translation: 'colors_tangerine-yellow'},
					{hex: '55aa22', name: 'Kelly Green', translation: 'colors_kelly-green'},
					{hex: '1799dd', name: 'Pacific Blue', translation: 'colors_pacific-blue'},
					{hex: '448888', name: 'Paradiso Teal', translation: 'colors_paradiso-teal'},
					{hex: 'ff8800', name: 'Dark Orange', translation: 'colors_dark-orange'}
				];
				const foundColor = colors.concat(deprecatedColors).find(
					(color)=> color.hex === marker.tag_color);
				translation += `, ${this.translate.instant('feedback-item_aria-label-tag', {
					firstTwoLetters: marker.tag_abbreviation ||
						marker.tag_name.substring(0, 2).toUpperCase(),
					color: this.translate.instant(foundColor ? foundColor.translation : 'colors_other-color'),
					text: marker.tag_name
				})}`;
			});
		}

		if (comment.text) {
			translation += `, ${this.strip(comment.text)}`;
		}

		if (comment.updated_by) {
			translation += `, ${this.translate.instant('feedback-session-feedback-tree-comment_ai-edited-by', {
				updated_by: comment.updated_by
			})}`;
		}

		if (comment.media) {
			comment.media.forEach((res) => {
				if (res.item.media_type === this.MediaModel.TYPE.VIDEO ||
					res.item.media_type === this.MediaModel.TYPE.AUDIO) {
					translation += `, ${this.translate.instant('feedback-item_aria-label-duration-media', {
						duration: this.formatTimePipe.transform(res.item.duration)
					})}`;
				}
				else if (res.item.media_type === this.MediaModel.TYPE.DOCUMENT && res.item.filename) {
					translation += `, ${this.translate.instant('feedback-item_aria-label-document', {
						type: res.item.filename.substr(res.item.filename.lastIndexOf('.') + 1).toLowerCase().toUpperCase()
					})}`;
				}
			});
		}

		if (comment.viewed_by_me) {
			translation += `, ${this.translate.instant('feedback-item_aria-label-viewed')}`;
		} else if (comment.directed_at_me) {
			translation += `, ${this.translate.instant('feedback-item_aria-label-directed-at-me')}`;
		} else {
			translation += `, ${this.translate.instant('feedback-item_aria-label-unviewed')}`;
		}
		this.calculatedValuesCache.set(comment.comment_id, { ...cachedValue, ariaLabel: translation });
		return translation;
	};

	private strip = (html) => {
		const doc = new DOMParser().parseFromString(html, 'text/html');
		return doc.body.textContent;
	};

	private getLibraryItemType (media: Media): CONTENT_TYPES {
		return media.media_type === this.MediaModel.TYPE.DOCUMENT
			? CONTENT_TYPES.DOCUMENTS
			: CONTENT_TYPES.MEDIA;
	}

	private showAddToLibraryErrorToast (messageKey: string = 'common_unknown-error_message'): void {
		this.ngxGoToastService.createToast({
			type: GoToastStatusType.ERROR,
			message: messageKey
		});
	}

	private handlePlayerSync (comments: ProcessedComment[]): void {
		this.clearPlayerSyncEvents();
		this.playerSyncCallback = this.onTimeReached.bind(this);

		comments.forEach((comment) => {
			this.playerSync.on(
				this.timeUtils.millisecondsToSeconds(comment.time),
				this.playerSyncCallback
			);
		});
	}

	private clearPlayerSyncEvents () {
		if (!this.playerSyncCallback) return;

		this.commentListService.comments$.pipe(take(1)).subscribe((comments) => {
			comments.forEach((comment) => {
				this.playerSync.off(
					this.timeUtils.millisecondsToSeconds(comment.time),
					this.playerSyncCallback
				);
			});
		});
	}

	private onTimeReached (time: number) {
		if (!this.cdr || (this.cdr as ViewRef).destroyed) return;

		this.commentListService.comments$.pipe(take(1)).subscribe((comments) => {
			const matchingComments = comments.filter(
				(item) =>
					item.time &&
					this.timeUtils.millisecondsToSeconds(item.time) ===
					this.timeUtils.millisecondsToSeconds(time)
			);

			if (matchingComments.length > 0) {
				if (this.autoScrollEnabled) {
					this.scrollToComment(matchingComments[0]);
				}

				const mediaResources = matchingComments
					.filter((comment) => comment.media && comment.media.length > 0)
					.reduce((acc, comment) => [...acc, ...comment.media], []);

				if (this.feedbackSessionService.getAutoPlayEnabled() && mediaResources.length > 0) {
					this.playerSync.halt();
					this.allPlayersService.playCommentResources(mediaResources).then(() => {
						this.playerSync.resume();
					});
				}

				const commentIds = matchingComments.map((comment) => comment.comment_id);
				this.activeCommentIds.push(...commentIds);
				this.cdr.detectChanges();

				timer(1500)
					.pipe(take(1))
					.subscribe(() => {
						if (this.cdr && !(this.cdr as ViewRef).destroyed) {
							this.activeCommentIds = this.activeCommentIds.filter(
								(id) => !commentIds.includes(id)
							);
							this.cdr.detectChanges();
						}
					});
			}
		});
	}

	private handleNewComment (comments: ProcessedComment[]): void {
		const currentCommentIds = comments.filter((c) => c.comment_id).map((c) => c.comment_id);

		if (currentCommentIds.length > this.previousCommentIds.length) {
			const newCommentId = currentCommentIds.find((id) => !this.previousCommentIds.includes(id));
			if (newCommentId) {
				const newComment = comments.find((c) => c.comment_id === newCommentId);
				if (newComment) {
					this.scrollToComment(newComment);
				}
			}
		}
		this.previousCommentIds = currentCommentIds;
	}

	private scrollToComment (comment: ProcessedComment): void {
		this.scrollToComment$.next(comment);
	}

	private performScrollToComment (comment: ProcessedComment): void {
		this.commentListService.comments$.pipe(take(1)).subscribe((comments) => {
			const index = comments.findIndex(
				(c) => c.comment_id === comment.comment_id || c.temp_id === comment.temp_id
			);

			if (index !== -1) {
				timer(50).pipe(takeUntil(this.destroy$)).subscribe(() => {
					const el = document.querySelector(`[data-comment-id="${comment.comment_id || comment.temp_id}"]`);
					// scroll if needed
					if (el) {
						const feedbackDisplay = this.elementRef.nativeElement.querySelector('cdk-virtual-scroll-viewport');
						const feedbackRect = feedbackDisplay.getBoundingClientRect();
						const rect = el.getBoundingClientRect();
						if (rect.top < feedbackRect.top || rect.bottom > feedbackRect.bottom) {
							if (comment.parent_id) {
								el.scrollIntoView({ behavior: 'smooth', block: 'end' });
							} else {
								el.scrollIntoView({ behavior: 'smooth', block: 'start' });
							}
						}
					} else {
						this.scrollStrategy.scrollToIndex(
							comments.findIndex((c) => c.comment_id === comment.comment_id),
							'auto'
						);
					}
				});
			}
		});
	}
}
